import React from "react";
// Relative, unlike its shadcn neighbours' "@/lib/utils", and for the same reason
// FieldError.jsx gives: this component is rendered by admin screens that are already
// under component test, and the "@" alias does not exist under Jest. Resolving for real
// beats making each of those suites declare a virtual passthrough mock.
import { cn } from "../../lib/utils";

/**
 * The single rendering of "this screen could not READ its data".
 *
 * WHY THIS EXISTS (BUG-051 / BUG-056). An admin screen holds two states by default —
 * "here is your data" and its initial value — and a failed read silently lands in the
 * second. So the screen makes a POSITIVE FALSE CLAIM: "No products yet", "No messages
 * yet", every site-image slot badged "Default", every copy field blank. A practitioner
 * who believes it re-types work that was never lost, or saves a blank over live copy.
 * A failed read is a THIRD state, and it has to be sayable.
 *
 * PRESENTATIONAL ON PURPOSE, exactly like FieldError: the caller passes the message it
 * has already resolved (`extractApiError(e, tv("errors.loadFailed"), tv)`) and the label
 * for the retry. That keeps this file free of `@/lib/api` and `@/lib/LanguageContext`,
 * neither of which can be imported for real under Jest (axios is ESM-only; the language
 * provider transitively imports it) — so this component is testable, and every screen
 * test that renders it exercises the real markup rather than a stub.
 *
 * Renders NOTHING without a message, so callers pass their error state straight in.
 *
 * @param {string} [message] resolved, user-visible text.
 * @param {() => void} [onRetry] re-runs the read. Omit only when the screen has no way to
 *   retry — a dead end is barely better than a lie, so this should be rare.
 * @param {string} [retryLabel] resolved label for that control.
 * @param {string} [testId] `data-testid`, by convention `${screenTestId}-load-error`.
 * @param {string} [retryTestId] `data-testid` for the retry control.
 * @param {string} [className] extra classes, merged (tailwind-merge wins on conflicts).
 */
export default function LoadFailure({
  message,
  onRetry,
  retryLabel,
  testId,
  retryTestId,
  className,
}) {
  if (!message) return null;
  return (
    <div
      role="alert"
      data-testid={testId}
      className={cn(
        "text-sm bg-red-50 border border-red-200 text-red-800 rounded-2xl p-6 text-center",
        className,
      )}
    >
      <p className="m-0">{message}</p>
      {onRetry && (
        <button type="button" onClick={onRetry} data-testid={retryTestId} className="mt-3 underline">
          {retryLabel}
        </button>
      )}
    </div>
  );
}
